import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiMapPin } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Careers.css';

// 招聘投递专用邮箱，与页脚/联系页的通用 hello@ 分开。
// 邮箱与语言无关，所以放在这里一份，而不是在 4 个 locale 里各存一份。
const HR_CONTACT_EMAIL = 'hr-contact@infini-nova.com';

function JobCard({ job, t }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`job-card ${open ? 'open' : ''}`}>
      <div className="job-header" onClick={() => setOpen(!open)} role="button" tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setOpen(!open)}>
        <div className="job-header-left">
          <div className="job-tags">
            <span className="job-tag dept">{job.departmentLabel || job.department}</span>
            <span className="job-tag type">{job.typeLabel || job.type}</span>
            <span className="job-tag type">{job.modeLabel || job.mode}</span>
            <span className="job-tag location"><FiMapPin style={{ verticalAlign: 'middle', marginRight: '3px' }} />{job.location}</span>
          </div>
          <h3 className="job-title">{job.title}</h3>
        </div>
        <div className="job-header-right">
          <FiChevronDown className="job-chevron" />
        </div>
      </div>

      {open && (
        <div className="job-detail">
          {job.summary && <p style={{ color: '#475569', marginBottom: '1.5rem' }}>{job.summary}</p>}
          <div className="job-detail-grid">
            <div className="job-section">
              <h4>{t('careers.responsibilities')}</h4>
              <ul>{(job.responsibilities || []).map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
            <div className="job-section">
              <h4>{t('careers.requirements')}</h4>
              <ul>{(job.requirements || []).map((r, i) => <li key={i}>{r}</li>)}</ul>
              {job.niceToHave && job.niceToHave.length > 0 && (
                <>
                  <h4 style={{ marginTop: '1.5rem' }}>{t('careers.niceToHave')}</h4>
                  <ul>{job.niceToHave.map((r, i) => <li key={i}>{r}</li>)}</ul>
                </>
              )}
            </div>
          </div>
          <div className="job-notice">
            <strong>{t('careers.noticeTitle')}: </strong>
            {t('careers.noticeText')}{' '}
            <a href={`mailto:${HR_CONTACT_EMAIL}`}><strong>{HR_CONTACT_EMAIL}</strong></a>{' '}
            {t('careers.noticeSuffix')}
          </div>
        </div>
      )}
    </div>
  );
}

const DEPT_FILTER_LABELS = {
  en:      { all: 'All Departments', operations: 'Operations & Admin', engineering: 'Engineering & Technology', consulting: 'Strategy & Consulting' },
  'zh-CN': { all: '全部部门', operations: '运营与行政', engineering: '工程与技术', consulting: '战略与咨询' },
  'zh-TW': { all: '全部部門', operations: '營運與行政', engineering: '工程與技術', consulting: '策略與顧問' },
  ja:      { all: '全部署', operations: 'オペレーション・管理', engineering: 'エンジニアリング・テクノロジー', consulting: '戦略・コンサルティング' }
};

const Careers = () => {
  const { t, i18n } = useTranslation('common');
  const lang = i18n.language || 'en';
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    const fetchCareers = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/careers?lang=${lang}`);
        setJobs(res.data.data || []);
      } catch (error) {
        console.error('Error fetching careers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCareers();
  }, [lang]);

  const culture = t('careers.culture', { returnObjects: true });
  const deptLabels = DEPT_FILTER_LABELS[lang] || DEPT_FILTER_LABELS.en;

  const filteredJobs = activeFilter === 'all'
    ? jobs
    : jobs.filter(j => j.department === activeFilter);

  const filters = [
    { key: 'all',        label: deptLabels.all },
    { key: 'operations', label: deptLabels.operations },
    { key: 'engineering',label: deptLabels.engineering },
    { key: 'consulting', label: deptLabels.consulting },
  ];

  return (
    <div className="careers-page">
      <section className="page-hero">
        <div className="container">
          <p className="page-label">{t('careers.heroLabel')}</p>
          <h1>{t('careers.heroTitle')}</h1>
          <p className="page-description">{t('careers.heroDescription')}</p>
        </div>
      </section>

      <div className="culture-strip">
        {Array.isArray(culture) && culture.map((item, i) => (
          <div key={i} className="culture-item">
            <div className="culture-icon">{item.icon}</div>
            <h3>{item.title}</h3>
            <p>{item.description}</p>
          </div>
        ))}
      </div>

      <section className="section">
        <div className="container">
          <div className="section-header">
            <p className="section-label">{t('careers.openingsLabel')}</p>
            <h2>{t('careers.openingsTitle')}</h2>
          </div>

          <div className="careers-filter">
            {filters.map(f => (
              <button
                key={f.key}
                className={`filter-btn ${activeFilter === f.key ? 'active' : ''}`}
                onClick={() => setActiveFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>

          <p className="jobs-count">
            <span>{filteredJobs.length}</span> {filteredJobs.length === 1 ? 'position found' : 'positions found'}
          </p>

          <div className="jobs-list">
            {loading ? (
              <div className="loading"><div className="spinner"></div></div>
            ) : filteredJobs.length > 0 ? (
              filteredJobs.map(job => <JobCard key={job._id || job.jobId} job={job} t={t} />)
            ) : (
              <div className="no-jobs"><p>{t('careers.noJobs')}</p></div>
            )}
          </div>

          <div className="careers-cta">
            <h3>{t('careers.ctaTitle')}</h3>
            <p>{t('careers.ctaText')}</p>
            <Link to="/contact" className="btn btn-primary">{t('careers.ctaButton')}</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
